import { onScopeDispose, ref, toValue, watch } from "vue";
import usersService from "@/services/usersService";
import { isNPIValid } from "@/utils/validators";
const LOOKUP_DEBOUNCE_MS = 400;
/** Digits of a US fax with an optional leading country code dropped: "1 (512) 555-0100" → "5125550100". */
export const usFaxDigits = (value) => String(value ?? "")
    .replace(/\D/g, "")
    .replace(/^1(?=\d{10}$)/, "");
/**
 * The account fax in the form's own shape. The backend returns whatever a send can dial (10 or 11 digits,
 * "+" allowed) while the form's isPhoneNumberValid wants exactly 10 digits and no "+", so an account fax is
 * re-expressed as AAA-BBB-CCCC before it is filled in — or dropped when it cannot be.
 */
const toFormFax = (value) => {
    const digits = usFaxDigits(value);
    return digits.length === 10 ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}` : null;
};
export function useRegisteredProviderAccount(npi, enabled) {
    const account = ref(null);
    // Every lookup takes a ticket and a newer NPI voids the ones before it — debounced or already on the
    // wire — so a slow answer for a previous NPI can never land on the form.
    let ticket = 0;
    let timer = null;
    async function lookup(digits, mine) {
        try {
            const result = await usersService.lookupProviderAccount(digits);
            if (mine !== ticket)
                return;
            account.value = result.registered ? { ...result, fax: toFormFax(result.fax) } : null;
        }
        catch {
            // A failed lookup proves nothing about the provider; the form keeps its own values.
            if (mine === ticket)
                account.value = null;
        }
    }
    watch([() => toValue(npi), () => toValue(enabled)], ([value, on]) => {
        ticket += 1;
        if (timer)
            clearTimeout(timer);
        account.value = null;
        const digits = String(value ?? "").replace(/\D/g, "");
        if (!on || digits.length !== 10 || !isNPIValid(digits))
            return;
        const mine = ticket;
        timer = setTimeout(() => lookup(digits, mine), LOOKUP_DEBOUNCE_MS);
    }, { immediate: true });
    onScopeDispose(() => {
        ticket += 1;
        if (timer)
            clearTimeout(timer);
    });
    return { account };
}
